import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  IconButton,
  Alert
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import AppLoader from '../../components/AppLoader';
import { fetchJson } from '../../utils/fetchJson';

export default function EvaluationTypesPage() {
  const { t } = useTranslation();

  // State
  // Context-aware cache key
  const schoolId = typeof window !== 'undefined' ? localStorage.getItem('schoolId') : null;
  const phase = typeof window !== 'undefined' ? (localStorage.getItem('schoolAdmin_activePhase') || '') : '';
  const isSecPhase = String(phase || '').trim().toLowerCase() === 'sec';
  const ayKey = schoolId ? `${schoolId}:currentAcademicYearId` : 'currentAcademicYearId';
  const ayId = typeof window !== 'undefined' ? (localStorage.getItem(ayKey) || localStorage.getItem('currentAcademicYearId') || '') : '';
  const cacheKey = `evaluation:types:${schoolId || 'ns'}|${phase || 'ns'}|${ayId || 'ns'}`;

  const cached = (() => { try { return JSON.parse(localStorage.getItem(cacheKey) || '[]'); } catch { return []; } })();

  const [types, setTypes] = useState(Array.isArray(cached) ? cached : []);
  const [loading, setLoading] = useState(false);
  const guardRef = useRef(null);
  const [error, setError] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentType, setCurrentType] = useState(null);

  // Form values
  const [formValues, setFormValues] = useState({
    type: "sequence",
    number: 1,
    name: "",
    state: "closed"
  });

  // Feedback
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  // Load types
  useEffect(() => {
    if ((types || []).length === 0) {
      guardRef.current = setTimeout(() => setLoading(false), 2000);
    }
    loadTypes();
    return () => { if (guardRef.current) clearTimeout(guardRef.current); };
  }, []);

  const loadTypes = async () => {
    setLoading(true);
    setError("");
    try {
      const phaseId = typeof window !== 'undefined' ? (localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '') : '';
      const phaseCode = typeof window !== 'undefined' ? (localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '') : '';
      const headers = {};
      if (phaseId) headers['X-Active-Phase-Id'] = String(phaseId);
      if (phaseCode) headers['X-Active-Phase'] = String(phaseCode);

      const data = await fetchJson(`/api/evaluation-types`, { method: 'GET', headers });
      const list = Array.isArray(data) ? data : (data.data || data.types || []);
      setTypes(list || []);
      try { localStorage.setItem(cacheKey, JSON.stringify(list || [])); } catch(_) {}
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Open Create dialog
  const openCreateDialog = () => {
    if (!isSecPhase) return;
    setCurrentType(null);
    setFormValues({ type: "mock", number: 1, name: "", state: "closed" });
    setDialogOpen(true);
  };

  // Open Edit dialog
  const openEditDialog = (type) => {
    setCurrentType(type);
    setFormValues({
      type: type.type || 'sequence',
      number: type.number || 1,
      name: type.name || '',
      state: type.state || 'closed'
    });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setCurrentType(null);
    setFormValues({ type: "sequence", number: 1, name: "", state: "closed" });
    setError("");
  };

  // Save/Create or Update
  const handleSave = async () => {
    setError("");
    const { type, number, name, state } = formValues;
    if (!name) {
      setError(t("evalTypesPage.fillName") || "Name is required");
      return;
    }
    try {
      const phaseId = typeof window !== 'undefined' ? (localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '') : '';
      const phaseCode = typeof window !== 'undefined' ? (localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '') : '';
      const headers = { "Content-Type": "application/json" };
      if (phaseId) headers['X-Active-Phase-Id'] = String(phaseId);
      if (phaseCode) headers['X-Active-Phase'] = String(phaseCode);

      if (currentType) {
        await fetchJson(`/api/evaluation-types/${currentType.id}`, {
          method: 'PUT',
          headers,
          body: { name, state }
        });
      } else {
        await fetchJson(`/api/evaluation-types`, {
          method: 'POST',
          headers,
          body: { type, number: Number(number), name, state }
        });
      }

      setSnackbarMsg(currentType
        ? t("evalTypesPage.updated")
        : t("evalTypesPage.created")
      );
      setSnackbarOpen(true);
      closeDialog();
      loadTypes();
    } catch (err) {
      setError(err.message);
    }
  };

  // Delete
  const handleDelete = async (id) => {
    if (!window.confirm(t("evalTypesPage.deleteConfirm") || "Delete this type?")) return;
    try {
      const phaseId = typeof window !== 'undefined' ? (localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '') : '';
      const phaseCode = typeof window !== 'undefined' ? (localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '') : '';
      const headers = {};
      if (phaseId) headers['X-Active-Phase-Id'] = String(phaseId);
      if (phaseCode) headers['X-Active-Phase'] = String(phaseCode);

      await fetchJson(`/api/evaluation-types/${id}`, { method: 'DELETE', headers });
      setSnackbarMsg(t("evalTypesPage.deleted"));
      setSnackbarOpen(true);
      loadTypes();
    } catch (err) {
      alert(err.message);
    }
  };

  // Columns
  const columns = [
    { field: "id", headerName: t("evalTypesPage.id") || "ID", width: 70 },
    { field: "type", headerName: t("evalTypesPage.type") || "Type", width: 110 },
    { field: "number", headerName: t("evalTypesPage.number") || "No", width: 90 },
    { field: "name", headerName: t("evalTypesPage.name") || "Name", flex: 1 },
    {
      field: "state",
      headerName: t("evalTypesPage.state") || "State",
      width: 110
    },
    {
      field: "actions",
      headerName: t("evalTypesPage.actions") || "Actions",
      width: 160,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<EditIcon fontSize="small" />}
            onClick={() => openEditDialog(params.row)}
          >
            {t("evalTypesPage.edit")}
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon fontSize="small" />}
            onClick={() => handleDelete(params.row.id)}
          >
            {t("evalTypesPage.delete")}
          </Button>
        </Box>
      )
    }
  ];

  return (
    <Box sx={{ p: { xs: 2, sm: 4 } }}>
      <Typography variant="h4" gutterBottom>
        {t('evaluationTypesPage.title') || 'Evaluation Types'}
      </Typography>

      {isSecPhase && (
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          sx={{ mb: 2 }}
          onClick={openCreateDialog}
        >
          {t("evalTypesPage.newType") || "New Type"}
        </Button>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && (types || []).length === 0 ? (
        <AppLoader variant="circular3" size={48} centered messages={[t('evalTypesPage.loading') || 'Loading evaluation types...']} />
      ) : (
        <div style={{ height: 500, width: "100%" }}>
          <DataGrid
            rows={types}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10]}
          />
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {currentType
            ? t("evalTypesPage.editType")
            : t("evalTypesPage.newType")}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {!currentType && (
            <TextField
              select
              label={t('evalTypesPage.type') || 'Type'}
              fullWidth
              sx={{ mb: 2 }}
              value={formValues.type}
              onChange={(e) =>
                setFormValues((prev) => ({ ...prev, type: e.target.value }))
              }
              SelectProps={{ native: true }}
            >
              <option value="mock">mock</option>
            </TextField>
          )}
          {!currentType && (
            <TextField
              type="number"
              label={t('evalTypesPage.number') || 'Number'}
              fullWidth
              sx={{ mb: 2 }}
              value={formValues.number}
              onChange={(e) =>
                setFormValues((prev) => ({ ...prev, number: Number(e.target.value) }))
              }
            />
          )}
          <TextField
            label={t("evalTypesPage.name") || "Name"}
            fullWidth
            sx={{ mb: 2 }}
            value={formValues.name}
            onChange={(e) =>
              setFormValues((prev) => ({ ...prev, name: e.target.value }))
            }
          />
          <TextField
            select
            label={t('evalTypesPage.state') || 'State'}
            fullWidth
            sx={{ mb: 2 }}
            value={formValues.state}
            onChange={(e) =>
              setFormValues((prev) => ({ ...prev, state: e.target.value }))
            }
            SelectProps={{ native: true }}
          >
            <option value="closed">closed</option>
            <option value="open">open</option>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>{t("evalTypesPage.cancel")}</Button>
          <Button variant="contained" onClick={handleSave}>
            {t("evalTypesPage.save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMsg}
        action={
          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            onClick={() => setSnackbarOpen(false)}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      />
    </Box>
  );
}
